import { useNavigation } from '@app/hooks/useNavigation';
import { useQuestionaireRoute } from '@app/hooks/useQuestionaireRoute';
import { useUserValues } from '@app/hooks/useUserValues';
import { Locales } from '@components/block/Header/LangSelect';
import { Loading } from '@components/ui';

import AllowedValueTypes from '@legalosApi/types/AllowedValueTypes';

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageStep as Step } from '../../utils';
import { Props as BaseProps } from '../Step';

import { useUserValuesData } from '@app/hooks/useUserValuesData';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import QuestStepChoice from '../components/quest-step-choice';

export type Props = BaseProps<Step>;

export function ChoiceStep(props: Props) {
  const { i18n } = useTranslation();
  const { user, principalCompany, questionnaires } = useUserWithCompany();
  const { getValue, userValuesLoading, userValues } = useUserValuesData();
  const { setNavigation } = useNavigation();
  const { question } = props.step;
  const { jwt, tokenInfo } = useQuestionaireRoute();
  const tooltip = question.tags.process.some((tag) => tag === 'TOOLTIP');

  async function onSelect(value: string) {
    await props
      .onUpdateQuestion(question._id, value, true, jwt, jwt ? (i18n.language.slice(0, 2) as Locales) : undefined)
      .then(() => {
        // show "saved" animation
        setNavigation((nav) => void (nav.showSavedAnimation = true));
      });
    props.onAdvance(props.step.id);
  }

  const [value, setValue] = useState<AllowedValueTypes>(question.value ? question.value : '');

  // this handles the automatic answering of AUTO questions while displaying the Loading component
  const [blocked, setBlocked] = useState(true);
  const { updateFromUserValue } = useUserValues();
  const hasCalledUpdateRef = useRef(false);
  useEffect(() => {
    // Wait for user values to load before attempting to update from them
    if (userValuesLoading || (user && !userValues && !tokenInfo) || (tokenInfo && !userValues)) {
      return;
    }

    // Only call updateFromUserValue once
    if (hasCalledUpdateRef.current) {
      return;
    }

    hasCalledUpdateRef.current = true;

    setBlocked(
      updateFromUserValue(
        user,
        principalCompany,
        tokenInfo,
        props.step,
        props.questionnaire,
        questionnaires.find((q) => q.questionnaireId === props.questionnaire._id),
        props.onUpdateQuestion,
        props.onAdvance,
        getValue,
        setValue
      )
    );
  }, [user, principalCompany, tokenInfo, userValues, userValuesLoading]);
  if (blocked)
    return (
      <div className="mt-24">
        <Loading />
      </div>
    );

  return (
    <QuestStepChoice
      tooltip={tooltip}
      options={question.options}
      questionText={question.questionText}
      description={question.description}
      value={question.value || value}
      onSelect={onSelect}
    />
  );
}
